import { Box, Card, Divider, Text, Title } from '@mantine/core';
import { StatusTabs, type StatusTab } from '../../components/StatusTabs';
import {
  adjustedCount,
  coachCapacityStatus,
  resolveDoNotMatchGroups,
  unrecognizedDoNotMatchCount,
} from '../../model/derive';
import { hasCoachData } from '../../model/defaults';
import type { DataTab } from '../../model/steps';
import { useSetup } from '../../SetupContext';
import { CoachCapacityTab } from './CoachCapacityTab';
import { DnmGroupsTab } from './DnmGroupsTab';
import { ParticipantsTab } from './ParticipantsTab';

interface ConfigureDataStepProps {
  tab: DataTab;
  onTabChange: (tab: DataTab) => void;
  onlyUnrecognizedDnm: boolean;
  onOnlyUnrecognizedDnmChange: (value: boolean) => void;
}

export function ConfigureDataStep({
  tab,
  onTabChange,
  onlyUnrecognizedDnm,
  onOnlyUnrecognizedDnmChange,
}: ConfigureDataStepProps) {
  const { session, config } = useSetup();
  const adjusted = adjustedCount(session.participants, config);
  const unrecognized = config.enforce.doNotMatch
    ? unrecognizedDoNotMatchCount(session.participants, config)
    : 0;
  const missing = resolveDoNotMatchGroups(
    config.doNotMatchGroups,
    session.participants
  ).notFound.length;
  const cap = coachCapacityStatus(
    session.coaches,
    session.slots,
    config,
    session.participants.length
  );

  const tabs: StatusTab<DataTab>[] = [
    {
      id: 'participants',
      label: 'Participants and timeslots',
      status:
        unrecognized > 0
          ? `${unrecognized} ${unrecognized === 1 ? 'email' : 'emails'} not recognized`
          : adjusted > 0
            ? `${adjusted} availability adjusted`
            : `${session.participants.length} participants`,
      ok: unrecognized === 0,
    },
    {
      id: 'dnm',
      label: 'Do-not-match groups',
      status: missing > 0 ? `${missing} to fix` : 'All matched',
      ok: missing === 0,
    },
    {
      id: 'coaches',
      label: 'Coach capacity',
      // Without coach data there's nothing to fix here: capacity is off.
      status: !hasCoachData(session)
        ? 'No coach data'
        : cap.level === 'ok'
          ? `${cap.total} groups covered`
          : 'Needs attention',
      ok: !hasCoachData(session) || cap.level === 'ok',
    },
  ];

  return (
    <Card withBorder p={0} radius="lg">
      <Box px={28} pt={24} pb={18}>
        <Title order={2} c="medleyGreen.8">
          Configure data
        </Title>
        <Text c="dimmed" mt={6} maw={700}>
          Make sure the data matching will use is right. You can adjust things
          here until you start matching.
        </Text>
      </Box>
      <Divider />
      <Box px={28} pt={20}>
        <StatusTabs
          tabs={tabs}
          value={tab}
          onChange={onTabChange}
          label="Data"
        />
      </Box>
      <Box px={28} py={24} role="tabpanel">
        {tab === 'participants' && (
          <ParticipantsTab
            onlyUnrecognized={onlyUnrecognizedDnm}
            onOnlyUnrecognizedChange={onOnlyUnrecognizedDnmChange}
          />
        )}
        {tab === 'dnm' && <DnmGroupsTab />}
        {tab === 'coaches' && <CoachCapacityTab />}
      </Box>
    </Card>
  );
}
